import { useQuery } from "@tanstack/react-query";

import type { Carriers } from "~/features/ar-dashboard/schemas/dashboard-schemas.ts";
import { carriersSchema } from "~/features/ar-dashboard/schemas/dashboard-schemas.ts";
import { apiRoutes } from "~/shared/services/api/api-routes.ts";
import { getAndValidateTraxAr } from "~/shared/services/api/fetch-client.ts";

import type { DashboardQueryParams } from "./dashboard-query-params.ts";
import { buildDashboardSearchParams } from "./dashboard-query-params.ts";

const emptyCarriers: Carriers = { carriers: [] };

type UseCarriersParams = Pick<
	DashboardQueryParams,
	| "ltlBrokerIds"
	| "primaryBrokerIds"
	| "customerIds"
	| "startDate"
	| "endDate"
	| "selectedCustomerId"
	| "search"
>;

export const useCarriers = (params: UseCarriersParams) => {
	const enabled = Boolean(params.startDate && params.endDate);

	const { data, isLoading, isFetching, error, refetch } = useQuery({
		queryKey: [
			"dashboardCarriers",
			params.ltlBrokerIds ?? [],
			params.primaryBrokerIds ?? [],
			params.customerIds ?? [],
			params.startDate ?? null,
			params.endDate ?? null,
			params.selectedCustomerId ?? null,
			params.search ?? "",
		],
		queryFn: async ({ signal }) => {
			const searchParams = buildDashboardSearchParams(params, {
				dateOnly: true,
			});
			const url = `${apiRoutes.DASHBOARD_CARRIERS}?${searchParams.toString()}`;
			const result = await getAndValidateTraxAr(url, carriersSchema, {
				signal,
			});
			if (!result.success) {
				throw new Error(result.error);
			}
			return result.data;
		},
		enabled,
		placeholderData: (previousData) => previousData,
		staleTime: Infinity,
		gcTime: Infinity,
		refetchOnWindowFocus: false,
		refetchOnMount: false,
		refetchOnReconnect: false,
	});

	return {
		data: data ?? emptyCarriers,
		isLoading,
		isFetching,
		error,
		refetch,
	};
};
